import { type HTMLElement, parse, type TextNode } from 'node-html-parser';

import type { ParsedElementType, ParsedLayout, SectionElementType } from '../types';

const CITATION_REGEX = /\[\^(\d+)\^\]/g;

/**
 * Parses HTML content (including incomplete streaming content) and returns typed JSON structure
 *
 * @param modelOutput - The dynamic layout schema content string (possibly incomplete)
 * @returns Typed array representing the parsed HTML structure
 */
export function parseLuminaTagsToJSON(modelOutput: string): ParsedLayout {
	if (!modelOutput || typeof modelOutput !== 'string') {
		return [];
	}

	// Remove incomplete opening tags at the end (like "<section class="test" without closing >)
	let cleanedContent = modelOutput.replace(/<[^>]*$/, '');

	// Remove inline citation indicators from response for now
	// https://atlassian.slack.com/archives/C09KC9NV23W/p1769026048635919?thread_ts=1769020398.811119&cid=C09KC9NV23W
	cleanedContent = cleanedContent.replace(CITATION_REGEX, '');

	// Remove unclosed carousel tags and their contents to prevent rendering children
	// before the carousel wrapper is complete (which causes visual flickering)
	const carouselOpenTags = cleanedContent.match(/<carousel[^>]*>/g);
	const carouselCloseTags = cleanedContent.match(/<\/carousel>/g);
	const hasUnclosedCarousel = (carouselOpenTags?.length || 0) > (carouselCloseTags?.length || 0);

	if (hasUnclosedCarousel) {
		// Find the last unclosed carousel and remove everything from that point onwards
		const lastCarouselIndex = cleanedContent.lastIndexOf('<carousel');
		if (lastCarouselIndex >= 0) {
			cleanedContent = cleanedContent.substring(0, lastCarouselIndex);
		}
	}

	try {
		const root = parse(cleanedContent);
		const body = root.querySelector('body') || root;

		const result: SectionElementType[] = [];
		body.childNodes.forEach((element) => {
			const jsonElement = elementToJson(element as HTMLElement | TextNode);
			if (jsonElement) {
				result.push(jsonElement as SectionElementType);
			}
		});

		return result;
	} catch {
		return [];
	}
}

function elementToJson(element: HTMLElement | TextNode): ParsedElementType | null {
	// node types: https://dom.spec.whatwg.org/#dom-node-nodetype
	if (element.nodeType === 3) {
		// text node
		const text = element.text.trim();
		return text ? text : null;
	}

	// not an element node: https://dom.spec.whatwg.org/#dom-node-nodetype
	if (element.nodeType !== 1) {
		return null;
	}

	const htmlElement = element as HTMLElement; // text node already processed
	const result: ParsedElementType = {
		tag: htmlElement.tagName.toLowerCase(),
	} as ParsedElementType;

	// Add attributes if they exist
	if (
		htmlElement.attributes &&
		Object.keys(htmlElement.attributes).length > 0 &&
		typeof result !== 'string'
	) {
		result.attributes = htmlElement.attributes;
	}

	// Process children
	const children: Array<ParsedElementType | string> = [];
	htmlElement.childNodes.forEach((child) => {
		// handles text + element nodes
		const childJson = elementToJson(child as HTMLElement | TextNode);
		if (childJson) {
			children.push(childJson);
		}
	});

	if (children.length > 0 && typeof result !== 'string') {
		result.children = children;
	}
	return result as ParsedElementType;
}
